import React from 'react';
import { Button } from './ui/button';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const Footer = () => {
  const { language } = useLanguage();
  
  // Language-specific phone numbers
  const phoneNumbers = {
    en: '(+44) 203 769 94 43',
    es: '(+34) 93 018 6626',
    fr: '(+33) 170 849 040',
    it: '(+39) 02 87 368 972'
  };
  
  const phoneNumber = phoneNumbers[language] || phoneNumbers.en;
  
  const getLocalizedPath = (path) => {
    if (language === 'es') return `/es${path}`;
    if (language === 'fr') return `/fr${path}`;
    return path;
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            {getTranslation(language, 'footer.ctaTitle')}
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            {getTranslation(language, 'footer.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={getLocalizedPath('/prices')}>
              <Button 
                size="lg" 
                className="bg-white text-green-700 hover:bg-gray-100 font-bold px-8"
              >
                {getTranslation(language, 'footer.getQuote')}
              </Button>
            </Link>
            <Link to={getLocalizedPath('/contact')}>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-green-700 font-bold px-8"
              >
                {getTranslation(language, 'footer.contactUs')}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* About */}
            <div>
              <h3 className="text-lg font-black mb-4">Oakham Football Camps</h3>
              <p className="text-sm text-gray-400 mb-4">
                {getTranslation(language, 'footer.about')}
              </p>
              <div className="flex space-x-3">
                <Facebook className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer transition-colors" />
                <Instagram className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer transition-colors" />
                <Twitter className="h-5 w-5 text-gray-400 hover:text-white cursor-pointer transition-colors" />
              </div>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-lg font-black mb-4">{getTranslation(language, 'footer.contact')}</h3>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-start">
                  <MapPin className="h-4 w-4 mr-2 flex-shrink-0 mt-1" />
                  <span>Oakham School, Oakham, Rutland, England, LE15 6DT</span>
                </li>
                <li className="flex items-start">
                  <Phone className="h-4 w-4 mr-2 flex-shrink-0 mt-1" />
                  <span>{phoneNumber}</span>
                </li>
                <li className="flex items-start">
                  <Mail className="h-4 w-4 mr-2 flex-shrink-0 mt-1" />
                  <span>info@oakhamfootballcamps.com</span>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-lg font-black mb-4">{getTranslation(language, 'footer.legal')}</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <Link to={getLocalizedPath('/terms')} className="hover:text-white transition-colors">
                    {getTranslation(language, 'footer.terms')}
                  </Link>
                </li>
                <li>
                  <Link to={getLocalizedPath('/privacy-policy')} className="hover:text-white transition-colors">
                    {getTranslation(language, 'footer.privacyPolicy')}
                  </Link>
                </li>
                <li>
                  <Link to={getLocalizedPath('/cookie-policy')} className="hover:text-white transition-colors">
                    {getTranslation(language, 'footer.cookiePolicy')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-black mb-4">{getTranslation(language, 'footer.quickLinks')}</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <Link to={getLocalizedPath('/about-oakham')} className="hover:text-white transition-colors">
                    {getTranslation(language, 'nav.aboutOakham')}
                  </Link>
                </li>
                <li>
                  <Link to={getLocalizedPath('/prices')} className="hover:text-white transition-colors">
                    {getTranslation(language, 'nav.prices')}
                  </Link>
                </li>
                <li>
                  <Link to={getLocalizedPath('/contact')} className="hover:text-white transition-colors">
                    {getTranslation(language, 'nav.contact')}
                  </Link>
                </li>
                <li>
                  <Link to={getLocalizedPath('/signup')} className="hover:text-white transition-colors">
                    {getTranslation(language, 'nav.signUp')}
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>{getTranslation(language, 'footer.copyright')}</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to={getLocalizedPath('/privacy-policy')} className="hover:text-white transition-colors">{getTranslation(language, 'footer.privacyPolicy')}</Link>
              <Link to={getLocalizedPath('/terms')} className="hover:text-white transition-colors">{getTranslation(language, 'footer.terms')}</Link>
              <Link to={getLocalizedPath('/cookie-policy')} className="hover:text-white transition-colors">{getTranslation(language, 'footer.cookiePolicy')}</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;